import { randomInt } from "./random-int";

/**
 * Sorts an array of objects alphabetically based on a specified property or value.
 *
 * @template T - The type of the objects in the array.
 * @param {Iteratee<T, string>} [iteratee] - The function used to extract the property or value to sort by. Defaults to converting non-string values to an empty string.
 * @returns {(a: T, b: T) => number} - The comparison function used for sorting.
 */
const sortAlphabetically =
    <T>(
        iteratee: Iteratee<T, string> = item =>
            typeof item === "string" ? item : "",
    ): ((a: T, b: T) => number) =>
    (a: T, b: T) =>
        iteratee(a).localeCompare(iteratee(b));

type Iteratee<T, Value = string | number> = (item: T) => Value;

/**
 * Sorts an array of numbers in ascending order.
 * @param a The first number to compare.
 * @param b The second number to compare.
 * @returns A negative number if `a` is less than `b`, a positive number if `a` is greater than `b`, or 0 if they are equal.
 */
const sortNumerically = (a: number, b: number) => a - b;

/**
 * Sorts an array of objects based on the provided iteratee function.
 * @param iteratee The function used to extract the value to compare from each object.
 * @param direction The sorting direction, either "ASCENDING" or "DESCENDING". Defaults to "ASCENDING".
 * @returns A comparator function that can be used with the `Array.sort()` method.
 */
const sortBy =
    <T>(
        iteratee: Iteratee<T> = item =>
            typeof item === "string" || typeof item === "number" ? item : "",
        /** Defaults to ascending order */
        direction: "ASCENDING" | "DESCENDING" = "ASCENDING",
    ) =>
    (a: T, b: T) => {
        const av = iteratee(a);
        const bv = iteratee(b);
        return typeof av === "number" && typeof bv === "number"
            ? direction === "ASCENDING"
                ? Number(av) - Number(bv)
                : Number(bv) - Number(av)
            : direction === "ASCENDING"
              ? `${av}`.localeCompare(`${bv}`)
              : `${bv}`.localeCompare(`${av}`);
    };

/**
 * Shuffles the items in an array.
 *
 * @template T The type of the items in the array.
 * @param items The array of items to shuffle.
 * @returns A new array with the items shuffled.
 */
const shuffle = <T>(items: T[]): T[] => {
    const shuffled = [...items];
    for (let index1 = shuffled.length - 1; index1 > 0; index1--) {
        const index2 = randomInt({ min: 0, max: index1 });
        [shuffled[index1], shuffled[index2]] = [
            shuffled[index2],
            shuffled[index1],
        ];
    }
    return shuffled;
};

// caveat: don't know how quick localeCompare
// caveat: if problem, check it out

/**
 * Splits a string into segments of characters and integers. \
 * E.g. "ab%€123jakjak56" => ["ab%€", "123", "jakjak", "56"]
 */
const splitStringIntoCharsAndIntegerSegments = (input: string): string[] => {
    const regex = /[^\d]+|\d+/g;
    return input.match(regex) || [];
};

/**
 * Sorts an array of objects alphabetically and numerically based on a specified property or value.
 *
 * @template T - The type of the objects in the array.
 * @param {Iteratee<T, string>} [iteratee] - The function used to extract the property or value to sort by. Defaults to converting non-string values to an empty string.
 * @returns {(a: T, b: T) => number} - The comparison function used for sorting.
 */
const sortAlphabeticallyAndNumerically =
    <T>(
        iteratee: Iteratee<T, string> = item =>
            typeof item === "string" ? item : "",
    ): ((a: T, b: T) => number) =>
    (a: T, b: T) => {
        const valueA = iteratee(a);
        const valueB = iteratee(b);
        const valueA_Split = splitStringIntoCharsAndIntegerSegments(valueA);
        const valueB_Split = splitStringIntoCharsAndIntegerSegments(valueB);

        // Compare each segment of the strings
        for (let i = 0; i < valueA_Split.length; i++) {
            const segmentA = valueA_Split[i];
            const segmentB = valueB_Split[i];

            // If both segments are integers, compare them as numbers
            if (!isNaN(Number(segmentA)) && !isNaN(Number(segmentB))) {
                const numA = Number(segmentA);
                const numB = Number(segmentB);
                if (numA !== numB) {
                    // If they are not equal
                    // return them to get them sorted
                    return numA - numB;
                }
            } else {
                // Otherwise compare them as strings
                const comparison = segmentA.localeCompare(segmentB);
                if (comparison !== 0) {
                    // Return them to get them sorted
                    return comparison;
                }
            }
        }

        // if the strings are equal
        return 0;
    };

/**
 * Sorts an array of objects based on whether they have items in an array.
 * @param iteratee
 * @returns A comparator function that can be used with the `Array.sort()` method.
 */
const sortByIfArrayHasItems =
    <T>(
        iteratee: Iteratee<T, Array<unknown>> = item =>
            Array.isArray(item) ? item : [],
    ): ((a: T, b: T) => number) =>
    (a: T, b: T) => {
        // If both items have no objects, keep their order
        const aa = iteratee(a);
        const bb = iteratee(b);
        if (aa.length === 0 && bb.length === 0) {
            return 0;
        }
        // If only `aa` has no objects, move it down
        else if (aa.length === 0 && bb.length > 0) {
            return 1;
        }
        // If only `bb` has no objects, move it down
        else if (aa.length > 0 && bb.length === 0) {
            return -1;
        }
        // If both have objects or both have the same number of objects, keep their order
        else {
            return 0;
        }
    };

export {
    shuffle,
    sortAlphabetically,
    sortAlphabeticallyAndNumerically,
    sortBy,
    sortByIfArrayHasItems,
    sortNumerically,
};
export type { Iteratee };
